import { useState } from 'react';
import { toErrorMessage } from '../helpers/error-message';

type UseAsyncActionOptions = {
  // Breadcrumb label logged to console on failure, e.g. 'receiving: abandon failed'.
  errorLabel: string;
  // Receives the human-readable failure message, and null when the action starts
  // (to clear a prior error). The caller owns the error state, so sibling actions
  // can share one slot (a single actionError) or keep dedicated ones.
  setError: (message: string | null) => void;
  // When true, run() is a no-op — mirrors a disabled button so a re-entrant or
  // read-only click can't fire the request.
  disabled?: boolean;
  // Runs once after the action resolves successfully.
  onSuccess?: () => void;
};

// useAsyncAction wraps a fire-and-forget async handler with the bookkeeping the
// receiving action screens all repeat: ignore disabled/re-entrant clicks, clear
// the prior error, log a breadcrumb, and surface a human-readable failure
// message. Returns the pending flag and the click handler; the caller owns where
// the error message and any success side effects land.
export function useAsyncAction(
  // The resolved value is ignored, so any Promise is accepted (e.g. service.retry
  // resolves the session, while service.abandon/resume resolve void).
  action: () => Promise<unknown>,
  { errorLabel, setError, disabled = false, onSuccess }: UseAsyncActionOptions
): { pending: boolean; run: () => Promise<void> } {
  const [pending, setPending] = useState(false);

  const run = async () => {
    if (disabled || pending) {
      return;
    }

    setError(null);
    setPending(true);
    try {
      await action();
      onSuccess?.();
    } catch (err) {
      console.error(errorLabel, err);
      setError(toErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return { pending, run };
}
